import React, { useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormHelperText, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useUlozPravidloMutation } from '../../api/JsdhNotifikaceService/service/IotZarizeniMutations';
import { castiPravidla, labelAkce, shrnutiPravidla, textRadkuPravidla, vetaPravidla } from './iotStav';
import {
    AKCE_PRO_TYP, maCasovac, pulsPovolen, type AkcePravidla, type IotKanal, type IotPravidlo, type KatalogModel, type TypKanalu,
} from '../../api/JsdhNotifikaceService/service/IotZarizeniService';

/** Parametr podle akce a typu (stejné meze jako BE AkcePravidla); null = akce parametr nemá. */
export const parametrAkce = (a: AkcePravidla, typ: TypKanalu): { label: string; min: number; max: number; vychozi: number | null; povinny: boolean } | null => {
    if (a === 'ZAPNOUT_NA_MINUT') return { label: 'Doba (minuty)', min: 1, max: 720, vychozi: 30, povinny: true };
    if (a === 'PULS') return { label: 'Délka pulsu (s)', min: 1, max: 3600, vychozi: 2, povinny: true };
    if (a === 'ZAPNOUT_DO_KONCE' && typ === 'light') return { label: 'Jas (%)', min: 1, max: 100, vychozi: 100, povinny: false };
    if (a === 'COVER_POZICE') return { label: 'Poloha (%)', min: 0, max: 100, vychozi: 50, povinny: true };
    return null;
};

/** Celý text řádku pravidla („Při poplachu: …“, spec UX IoT 1.3); části vrací castiPravidla. */
export const popisPravidla = (typ: TypKanalu, p: IotPravidlo | null): string => textRadkuPravidla(typ, p);

export { castiPravidla, labelAkce, vetaPravidla };

/** Akce v nabídce: Puls jen podle stejného pravidla jako v menu Ovládat, uložený puls se ukáže vždy. */
export const akceVNabidce = (typ: TypKanalu, model: KatalogModel | undefined, ulozena: AkcePravidla | undefined): AkcePravidla[] =>
    AKCE_PRO_TYP[typ].filter((a) => a !== 'PULS' || pulsPovolen(model) || ulozena === 'PULS');

const PravidloDialog: React.FC<{ zarizeniId: number; kanal: IotKanal; model?: KatalogModel; onClose: () => void }> = ({ zarizeniId, kanal, model, onClose }) => {
    const ulozit = useUlozPravidloMutation();
    const typ = kanal.typ;
    const nabidka = akceVNabidce(typ, model, kanal.pravidlo?.akce);
    const [p, setP] = useState<IotPravidlo>(kanal.pravidlo ?? {
        akce: nabidka[0], parametr: null, vcetneTestu: false, poKonci: 'VRATIT', aktivni: true,
    });
    const [potvrditOdebrani, setPotvrditOdebrani] = useState(false);
    const par = parametrAkce(p.akce, typ);
    const parametrOk = !par || (p.parametr == null ? !par.povinny
        : Number.isInteger(p.parametr) && p.parametr >= par.min && p.parametr <= par.max);
    const volbaKonce = typ !== 'cover' && p.akce !== 'ZAPNOUT_NA_MINUT' && p.akce !== 'PULS';
    const kUlozeni: IotPravidlo = { ...p, poKonci: volbaKonce ? p.poKonci : 'NECHAT' };
    return (
        <>
            <Dialog open onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>Co udělat při poplachu: {kanal.nazev}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField select label="Akce při poplachu" value={p.akce}
                            onChange={(e) => { const akce = e.target.value as AkcePravidla; setP({ ...p, akce, parametr: parametrAkce(akce, typ)?.vychozi ?? null }); }}>
                            {nabidka.map((a) => <MenuItem key={a} value={a}>{labelAkce(a, typ)}</MenuItem>)}
                        </TextField>
                        {par && (
                            <TextField type="number" label={par.label} value={p.parametr ?? ''} error={!parametrOk}
                                helperText={`${par.min} až ${par.max}${p.akce === 'COVER_POZICE' ? '. Funguje jen u kalibrované rolety.' : ''}`}
                                onChange={(e) => setP({ ...p, parametr: e.target.value === '' ? null : Number(e.target.value) })} />
                        )}
                        {typ === 'switch' && maCasovac(p.akce) && kanal.obnovitPoVypadku !== false && (
                            <Typography variant="body2" color="text.secondary">
                                Obnova stavu po výpadku proudu se u kanálu vypne, protože pravidlo používá časovač. Po výpadku proudu tak
                                výstup zůstane vypnutý. Ručně ji jde znovu zapnout.
                            </Typography>
                        )}
                        {typ === 'cover' && <Typography variant="body2" color="text.secondary">Roleta se po poplachu nevrací do původní polohy.</Typography>}
                        {volbaKonce && (
                            <TextField select label="Po skončení poplachu" value={p.poKonci}
                                onChange={(e) => setP({ ...p, poKonci: e.target.value as 'VRATIT' | 'NECHAT' })}>
                                <MenuItem value="VRATIT">Vrátit stav před poplachem</MenuItem>
                                <MenuItem value="NECHAT">Nechat, jak je</MenuItem>
                            </TextField>
                        )}
                        <FormControlLabel control={<Switch checked={p.vcetneTestu} onChange={(e) => setP({ ...p, vcetneTestu: e.target.checked })} />}
                            label="Provést i při testovacím poplachu" />
                        <Box>
                            <FormControlLabel control={<Switch checked={p.aktivni} onChange={(e) => setP({ ...p, aktivni: e.target.checked })} />}
                                label="Pravidlo zapnuté" />
                            <FormHelperText sx={{ mt: 0 }}>Vypnuté pravidlo se při poplachu neprovede, nastavení zůstane uložené.</FormHelperText>
                        </Box>
                        <Typography variant="body2" color="text.secondary" data-testid="pravidlo-shrnuti">
                            Shrnutí: {shrnutiPravidla(kanal.nazev, typ, kUlozeni)}
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between' }}>
                    <Box>
                        {kanal.pravidlo && (
                            <Button color="error" disabled={ulozit.isPending} onClick={() => setPotvrditOdebrani(true)}>Odebrat pravidlo</Button>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={onClose}>Zrušit</Button>
                        <Button variant="contained" disabled={ulozit.isPending || !parametrOk}
                            onClick={() => ulozit.mutate({ id: zarizeniId, kanalId: kanal.id, data: kUlozeni }, { onSuccess: onClose })}>Uložit</Button>
                    </Box>
                </DialogActions>
            </Dialog>
            <Dialog open={potvrditOdebrani} onClose={() => setPotvrditOdebrani(false)}>
                <DialogTitle>Odebrat pravidlo pro {kanal.nazev}?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Při poplachu se pak s výstupem nic nestane. Pokud ho chcete jen dočasně vypnout, použijte přepínač Pravidlo zapnuté.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" autoFocus onClick={() => setPotvrditOdebrani(false)}>Zrušit</Button>
                    <Button color="error" disabled={ulozit.isPending}
                        onClick={() => ulozit.mutate({ id: zarizeniId, kanalId: kanal.id, data: null }, {
                            onSuccess: () => { setPotvrditOdebrani(false); onClose(); },
                            onError: () => setPotvrditOdebrani(false),
                        })}>Odebrat</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PravidloDialog;
